<template>
  <b-row v-if="reqItem" class="px-2 pb-2">
    <b-col md="12" class="mb-2">
      <div class="stats-card">
        <div class="stat-item">
          <span class="stat-label">REQUEST TYPE</span>
          <b-badge variant="light-primary" class="stat-value text-capitalize">
            {{ reqItem.type }}
          </b-badge>
        </div>
        <div class="stat-item" v-if="reqItem.meta_dues">
          <span class="stat-label">REMAINING DUES</span>
          <b-badge variant="light-danger" class="stat-value">
            {{ reqItem.meta_dues }}
          </b-badge>
        </div>
        <div class="stat-item">
          <span class="stat-label">REQUESTED ON</span>
          <b-badge variant="light-primary" class="stat-value">
            {{
              new Date(reqItem.request_date).toLocaleDateString("en-UK", {
                year: "numeric",
                day: "numeric",
                month: "short",
              })
            }}
          </b-badge>
        </div>
        <div class="stat-item">
          <span class="stat-label">STATUS</span>
          <b-badge :variant="getVariant" class="stat-value text-capitalize">
            {{ reqItem.status }}
          </b-badge>
        </div>
      </div>
    </b-col>

    <b-col md="12">
      <div class="form-group custom-form-group">
        <label class="font-weight-bolder">{{
          showStructuredNewAdmissionReason ? "Details" : "Reason"
        }}</label>
        <div
          v-if="showStructuredNewAdmissionReason"
          class="new-admission-reason-grid"
        >
          <div
            v-for="(row, idx) in structuredNewAdmissionReasonRows"
            :key="'na-reason-' + idx"
            class="new-admission-reason-row"
          >
            <span class="na-reason-label">{{ row.label }}</span>
            <span class="na-reason-value">{{ row.value || "—" }}</span>
          </div>
        </div>
        <b-form-textarea
          v-else
          class="modern-textarea"
          placeholder=""
          rows="3"
          v-model.trim="reqItem.reason"
          disabled
        />
      </div>
    </b-col>

    <b-col md="12" v-if="reqItem.status != 'pending'">
      <div class="form-group custom-form-group">
        <label class="font-weight-bolder">Note</label>
        <b-form-textarea
          class="modern-textarea"
          placeholder="Enter note"
          rows="3"
          v-model.trim="reqItem.note"
          disabled
        />
      </div>
    </b-col>
    <b-col md="12">
      <div class="d-flex justify-content-between">
        <label class="bv-no-focus-ring col-form-label pt-0">Documents</label>
      </div>
      <p v-if="docList.length == 0" class="font-weight-bold text-center">
        No documents found
      </p>
      <div v-else class="d-flex flex-wrap">
        <div
          class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
          style="width: 110px; height: 96px; position: relative"
          v-for="(file, index) in docList"
          :key="index"
          @click="openFile(file)"
        >
          <b-img
            v-if="isImage(file)"
            :src="getDocPreviewUrl(file)"
            class="doc-thumb-img"
            alt="preview"
            referrerpolicy="no-referrer"
            @error="onDocPreviewError($event, file)"
          />
          <feather-icon
            v-else
            icon="FileTextIcon"
            size="30"
            class="text-primary"
          />
        </div>
      </div>
    </b-col>
    <b-col md="12" v-if="reqItem.status == 'accepted'">
      <div class="d-flex justify-content-between">
        <label class="bv-no-focus-ring col-form-label pt-0"
          >Approval Documents</label
        >
      </div>
      <p v-if="appDocs.length == 0" class="font-weight-bold text-center">
        No documents found
      </p>
      <div v-else class="d-flex flex-wrap">
        <div
          class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
          style="width: 110px; height: 96px; position: relative"
          v-for="(file, index) in appDocs"
          :key="index"
          @click="openFile(file)"
        >
          <b-img
            v-if="isImage(file)"
            :src="getDocPreviewUrl(file)"
            class="doc-thumb-img"
            alt="preview"
            referrerpolicy="no-referrer"
            @error="onDocPreviewError($event, file)"
          />
          <feather-icon
            v-else
            icon="FileTextIcon"
            size="30"
            class="text-primary"
          />
        </div>
      </div>
    </b-col>
  </b-row>
</template>
<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BBadge,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BMediaAside,
  BSpinner,
  BSidebar,
  BInputGroup,
  BFormTextarea,
  BImg,
} from "bootstrap-vue";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
  isImageUrl,
} from "@/utils/gdriveUpload";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BBadge,
    BFormCheckbox,
    BFormGroup,
    BForm,
    BMediaAside,
    BSpinner,
    BSidebar,
    BInputGroup,
    BFormTextarea,
    BImg,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    reqItem: {
      type: Object,
      default: null,
    },
  },
  computed: {
    getVariant() {
      if (this.reqItem.status == "accepted") return "success";
      else if (this.reqItem.status == "pending") return "warning";
      else return "danger";
    },
    reportDomain() {
      const r = this.$store.state.report;
      return typeof r === "string" ? r.replace(/_/g, "") : "";
    },
    isTcfReport() {
      return ["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain);
    },
    /** Same separator as leavingRequests.vue buildNewAdmissionReasonFromForm */
    newAdmissionReasonSeparator() {
      return " || ";
    },
    showStructuredNewAdmissionReason() {
      return (
        this.reqItem &&
        this.reqItem.type === "new-admission" &&
        this.isTcfReport &&
        typeof this.reqItem.reason === "string" &&
        this.reqItem.reason.includes(this.newAdmissionReasonSeparator)
      );
    },
    structuredNewAdmissionReasonRows() {
      if (!this.showStructuredNewAdmissionReason) return [];
      const sep = this.newAdmissionReasonSeparator;
      const raw = (this.reqItem.reason || "").trim();
      return raw
        .split(sep)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((part) => {
          // Split on first ":" only — empty fields become "Label:" after trim(),
          // so ": " lookup fails and wrongly showed label "Detail".
          const i = part.indexOf(":");
          if (i === -1) return { label: "Detail", value: part };
          return {
            label: part.slice(0, i).trim(),
            value: part.slice(i + 1).trim(),
          };
        });
    },
    docList() {
      if (this.reqItem.document) return this.reqItem.document.split(",");
      else return [];
    },
    appDocs() {
      if (this.reqItem.approved_document)
        return this.reqItem.approved_document.split(",");
      else return [];
    },
  },
  methods: {
    isImage(url) {
      return isImageUrl(url);
    },
    getDocPreviewUrl(url) {
      return getMediaPreviewUrl(url, "Image");
    },
    onDocPreviewError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },
    openFile(url) {
      const target = isImageUrl(url)
        ? getMediaPreviewUrl(url, "Image")
        : url;
      window.open(target, "_blank");
    },
  },
};
</script>
<style lang="scss" scoped>
.stats-card {
  background: #fdfcff;
  border-radius: 12px;
  border: 1px solid #e0d8ff;
  padding: 1rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  .stat-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #6e6b7b;
    letter-spacing: 0.5px;
  }

  .stat-value {
    padding: 0.35rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 600;
    border-radius: 6px;
  }
}

.custom-form-group {
  margin-bottom: 1.25rem;

  label {
    color: #5e5873;
    font-size: 0.85rem;
    margin-bottom: 0.4rem;
  }

  .modern-textarea {
    border-radius: 10px;
    border: 1px solid #e0d8ff;
    padding: 0.75rem;
    background: #fff !important;
    color: #5e5873 !important;
    resize: none;
    font-weight: 500;
    font-size: 0.9rem;
    opacity: 1 !important;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 0.15rem rgba(115, 103, 240, 0.15);
    }
  }
}

.new-admission-reason-grid {
  border-radius: 10px;
  border: 1px solid #e0d8ff;
  background: #fff;
  overflow: hidden;
}

.new-admission-reason-row {
  display: grid;
  grid-template-columns: minmax(140px, 38%) 1fr;
  gap: 0.5rem 0.75rem;
  padding: 0.65rem 0.85rem;
  font-size: 0.9rem;
  border-bottom: 1px solid #f0edff;

  &:last-child {
    border-bottom: none;
  }
}

.na-reason-label {
  font-weight: 600;
  color: #6e6b7b;
  font-size: 0.75rem;
  letter-spacing: 0.3px;
}

.na-reason-value {
  color: #5e5873;
  font-weight: 500;
  word-break: break-word;
}

.doc-thumb-img {
  width: 100%;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
}
</style>
